import React, { Fragment, useState, useContext, useCallback } from "react";
import {connect} from "react-redux";
import {injectIntl} from "react-intl";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import UIPopupContext from "../../popups/UIPopup/UIPopupContext";
import {updatePromptParts, getCurrentPromptParts} from "../aiApi/aiApi";
import LoadingSpinner from "../../ui/loadingSpinner";
import TextInput from "../../ui/textInput";
import {hideDevGroupObject} from "../aiFunctions";
import UiPopupAIContentEditingModal from "./UiPopupAIContentEditingModal";
import {useAppDispatch, useAppSelector} from "../../../storeHooks";
import {setUiPopupState} from "../../../actions/actionFunctions";
import {getPromptFlagKeys, getPromptTypesSharingFlags} from "../aiPromptTypesConstants";

const UiPopupAIContentFeatureGroups = (props) => {

  const {type} = props;

  const dispatch = useAppDispatch();
  const {uiPopupState} = useAppSelector(state => state.messageBoxState);

  const {uiPopupContext, uiPopupDispatch} = useContext(UIPopupContext);
  const {groupObjects, promptsList, showAllFeatureGroups} = uiPopupContext;

  const [editingGroupObject, setEditingGroupObject] = useState(null);

  const isActiveType = "isActive" + type;
  const isActiveBody = "partBody" + type;
  const promptsPartsLoading = "promptsPartsLoading" + type;
  const savedPromptPartObject = "savedPromptPartObject" + type;
  const {enabledKey, sendThumbnailKey} = getPromptFlagKeys(type);

  const onCheckboxClick = useCallback((gr, flag = "enabled") => {
    const property = flag === "enabled" ? enabledKey : sendThumbnailKey;
    const newValue = !gr[property];
    const updatedGroupObject = {...gr, [property]: newValue};
    uiPopupDispatch({
      groupObjects: groupObjects.map((groupObject) => {
        if (groupObject.partId === gr.partId) {
          return updatedGroupObject;
        }
        return groupObject;
      }),
    });
    // the flag properties are shared by the prompt types of this tab (all three of them on the AI
    // Management tab, SqftAI alone on the SQFT one - see getPromptFlagKeys), so the new value is
    // written to the active prompt part of each of those. A type with no prompt selected yet is
    // skipped rather than stopping the loop.
    for (const promptType of getPromptTypesSharingFlags(type)) {
      const activePrompt = (promptsList || []).find((p) => p["isActive" + promptType]);
      if (!activePrompt) continue;
      updatePromptParts(
        updatedGroupObject,
        activePrompt,
        promptType
      );
    }
  }, [groupObjects, promptsList, uiPopupDispatch, type, enabledKey, sendThumbnailKey]);

  const handleOpenEditor = (groupObject) => {
    dispatch(setUiPopupState({
      ...uiPopupState,
      style: {maxWidth: "98%", maxHeight: "95%"}
    }));
    setEditingGroupObject(groupObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const handleCloseEditor = async (newValue) => {
    const groupObject = editingGroupObject;
    setEditingGroupObject(null);

    dispatch(setUiPopupState({
      ...uiPopupState,
      style: {maxWidth: "98%", maxHeight: "95%"}
    }));

    if (!groupObject) return;

    // 1) update value in context
    uiPopupDispatch({
      groupObjects: groupObjects.map((gr) => {
        if (gr.partId === groupObject.partId) {
          return { ...gr, [isActiveBody]: newValue };
        }
        return gr;
      }),
    });

    // 2) persist (same logic you had onBlur)
    const activePrompt = promptsList.find((p) => p[isActiveType]);
    if (!activePrompt) return;

    // updatePromptParts expects the updated body on the groupObject param,
    // so pass a patched copy (safer than relying on async state updates)
    const wasCreate = !groupObject[savedPromptPartObject];
    await updatePromptParts(
      { ...groupObject, [isActiveBody]: newValue },
      activePrompt,
      type
    );

    // api/PromptParts/list (create) returns nothing, so after a create we
    // re-fetch the parts and store the newly created one back into context.
    // Otherwise the next edit before a reload would not find
    // groupObject[savedPromptPartObject] and would create a duplicate instead
    // of updating it. (Same matching logic as UiPopupAIContentPrompts.js.)
    if (wasCreate) {
      try {
        const prPartsResponse = await getCurrentPromptParts(activePrompt);
        const fgName = groupObject.featureGroup.dbName || groupObject.featureGroup.partName;
        const createdPart =
          prPartsResponse.find((prPart) => prPart.featureGroup === fgName && prPart.partBody) ||
          prPartsResponse.find((prPart) => prPart.featureGroup === fgName) ||
          null;
        if (createdPart) {
          uiPopupDispatch((state) => ({
            ...state,
            groupObjects: (state.groupObjects || []).map((gr) => {
              if (gr.partId === groupObject.partId) {
                return {
                  ...gr,
                  [isActiveBody]: createdPart.partBody,
                  [savedPromptPartObject]: createdPart,
                  [sendThumbnailKey]: gr.isColorOnlyPromptPart ? false : createdPart.sendThumbnail,
                  [enabledKey]: gr.isColorOnlyPromptPart ? true : createdPart.enabled,
                };
              }
              return gr;
            }),
          }));
        }
      } catch (e) {
        console.log("handleCloseEditor: failed to refetch created prompt part", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  return (
    <Fragment>
      <ul className="ass-list u-pr-1 u-w-full">
        {groupObjects
          ? groupObjects.map((groupObject) => {
            if (hideDevGroupObject(groupObject, showAllFeatureGroups)) return null;

            // "<Group Name>_ColorOnly" rows (see getColorOnlyGroupObjects) only carry a prompt
            // body - no enabled / Send Thumbnail checkboxes - so the label is indented under the
            // group it belongs to and the Send Thumbnail column is left empty, keeping the prompt
            // inputs roughly aligned with the rest of the rows.
            if (groupObject.isColorOnlyPromptPart) {
              return (
                <li key={groupObject.featureGroup.partId} className="ass-list__item">
                  <span
                    style={{ width: "180px", flex: "0 0 180px", paddingLeft: "32px", marginRight: "5px", lineHeight: 1, display: "flex" }}
                    className="ll-text bold color-primary text-left h-full items-center"
                  >
                    {groupObject.featureGroup.dbName}
                  </span>

                  {uiPopupContext[promptsPartsLoading] ? (
                    <LoadingSpinner size=" message-loading-left-panel-spinner message-loading-left-panel-spinner--30 u-w-3" />
                  ) : (
                    <TextInput
                      className={"ui-input ui-input--new ui-input--gray"}
                      name={groupObject.partId}
                      value={groupObject[isActiveBody] || ""}
                      onFocus={() => handleOpenEditor(groupObject)}
                      onClick={() => handleOpenEditor(groupObject)}
                      onChange={() => null}
                      readOnly
                    />
                  )}
                  <span style={{ width: "180px", flex: "0 0 180px", margin: "0 0 0 16px" }} />
                </li>
              );
            }

            return (
              <li key={groupObject.featureGroup.partId} className="ass-list__item">
                <FormControlLabel
                  style={{ width: "180px", flex: "0 0 180px", lineHeight: 1, display: "flex" }}
                  className="ll-text bold color-primary text-left h-full items-center"
                  label={groupObject.featureGroup.dbName || groupObject.featureGroup.partName}
                  control={
                    <Checkbox
                      checked={groupObjects.some(
                        (gr) => gr.partId === groupObject.partId && gr[enabledKey]
                      )}
                      onChange={() => onCheckboxClick(groupObject, "enabled")}
                    />
                  }
                />

                {uiPopupContext[promptsPartsLoading] ? (
                  <LoadingSpinner size=" message-loading-left-panel-spinner message-loading-left-panel-spinner--30 u-w-3" />
                ) : (
                  <TextInput
                    className={"ui-input ui-input--new ui-input--gray"}
                    name={groupObject.partId}
                    value={groupObject[isActiveBody] || ""}
                    // open modal on focus/click
                    onFocus={() => handleOpenEditor(groupObject)}
                    onClick={() => handleOpenEditor(groupObject)}
                    onChange={() => null}
                    // prevent typing in the tiny input
                    readOnly
                  />
                )}
                <FormControlLabel
                  style={{ width: "180px", flex: "0 0 180px", margin: "0 0 0 16px", height: "40px" }}
                  className="ll-text bold color-primary text-left"
                  label="Send Thumbnail"
                  control={
                    <Checkbox
                      checked={!groupObjects.some(
                        (gr) => gr.partId === groupObject.partId && gr[sendThumbnailKey] === false
                      )}
                      onChange={() => onCheckboxClick(groupObject, "sendThumbnail")}
                    />
                  }
                />
              </li>
            );
          })
          : null}
      </ul>

      <UiPopupAIContentEditingModal
        propsOpened={!!editingGroupObject}
        propsValue={editingGroupObject?.[isActiveBody] || ""}
        propsTitle={
          editingGroupObject?.featureGroup?.dbName || editingGroupObject?.featureGroup?.partName || ""
        }
        propsClose={handleCloseEditor}
      />
    </Fragment>
  );
};

const mapStateToProps = (store) => {
  const fabricatorData = store.fabricatorState;

  return {
    fabricatorData,
  };
};

export default injectIntl(
  connect(mapStateToProps)(UiPopupAIContentFeatureGroups)
)

